import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Ticket, Clock, Trophy, CheckCircle2, X } from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import LuckyPackWinShareDialog from './LuckyPackWinShareDialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtCT = (iso) => {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleString('en-US', {
            timeZone: 'America/Chicago',
            month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
        }) + ' CT';
    } catch { return ''; }
};

/**
 * "Lucky 5" tiered sports lottery banner.
 *
 * Player-facing card that:
 *   - Shows the active 5-pick pack with payout tiers
 *   - Lets the player enter once per pack for a fixed fee
 *   - After settlement shows hits + payout status
 *
 * Hides itself when no pack is active. Polls every 60s so it appears /
 * disappears automatically.
 */
const LuckyPackBanner = ({ onEntered }) => {
    const { t } = useTranslation();
    const [pack, setPack] = useState(null);
    const [myEntry, setMyEntry] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [countdown, setCountdown] = useState('');

    const load = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/lucky-pack/active`);
            setPack(data?.pack || null);
            setMyEntry(data?.my_entry || null);
        } catch {
            setPack(null);
            setMyEntry(null);
        }
    }, []);

    useEffect(() => {
        load();
        const id = setInterval(load, 60_000);
        return () => clearInterval(id);
    }, [load]);

    // Countdown to first kickoff (entry cutoff)
    useEffect(() => {
        if (!pack?.picks?.length) { setCountdown(''); return undefined; }
        const earliest = pack.picks
            .map(p => p.commence_time)
            .filter(Boolean)
            .sort()[0];
        if (!earliest) { setCountdown(''); return undefined; }
        const tick = () => {
            const ms = new Date(earliest).getTime() - Date.now();
            if (ms <= 0) { setCountdown(t('lucky_pack.entries_closed')); return; }
            const mins = Math.floor(ms / 60_000);
            if (mins < 60) { setCountdown(t('lucky_pack.closes_in', { when: `${mins}m` })); return; }
            const hrs = Math.floor(mins / 60);
            const m = mins % 60;
            if (hrs < 24) { setCountdown(t('lucky_pack.closes_in', { when: `${hrs}h ${m}m` })); return; }
            const days = Math.floor(hrs / 24);
            setCountdown(t('lucky_pack.closes_in', { when: `${days}d` }));
        };
        tick();
        const id = setInterval(tick, 30_000);
        return () => clearInterval(id);
    }, [pack, t]);

    const submit = async () => {
        if (!pack) return;
        setSubmitting(true);
        try {
            await axios.post(`${API}/lucky-pack/enter`, { pack_id: pack.id });
            toast.success(t('lucky_pack.entered_toast'));
            setOpenDialog(false);
            await load();
            if (onEntered) onEntered();
        } catch (e) {
            toast.error(e.response?.data?.detail || t('lucky_pack.enter_error'));
        }
        setSubmitting(false);
    };

    if (!pack) return null;

    const payouts = pack.payouts || {};
    const p3 = Number(payouts['3'] || 0);
    const p4 = Number(payouts['4'] || 0);
    const p5 = Number(payouts['5'] || 0);
    const entry = Number(pack.entry_price || 5);

    const sideLabel = (p) => {
        const m = (p.market || 'h2h').toLowerCase();
        if (m === 'spreads' && p.point != null) {
            const pt = p.point > 0 ? `+${p.point}` : `${p.point}`;
            return `${p.outcome_name} ${pt}`;
        }
        if (m === 'totals' && p.point != null) {
            return `${p.outcome_name} ${p.point}`;
        }
        return p.outcome_name;
    };

    const hasEntered = !!myEntry;
    const isSettled = myEntry && ['won', 'lost', 'refund'].includes(myEntry.status);

    return (
        <>
            <AnimatePresence>
                <motion.div
                    key={pack.id}
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    className="relative w-full overflow-hidden rounded-2xl border p-4 sm:p-5"
                    style={{
                        background: 'linear-gradient(135deg, rgba(57,255,20,0.10) 0%, rgba(255,215,0,0.10) 50%, rgba(255,59,128,0.10) 100%)',
                        borderColor: 'rgba(255,215,0,0.45)',
                    }}
                    data-testid="lucky-pack-banner"
                >
                    {/* Animated edge */}
                    <motion.div
                        aria-hidden="true"
                        className="absolute inset-y-0 left-0 w-2"
                        style={{ background: 'linear-gradient(180deg, #FFD700, #FF007F)' }}
                        animate={{ opacity: [0.6, 1, 0.6] }}
                        transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
                    />

                    <div className="relative pl-3">
                        {/* Header */}
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-[#FFD700] bg-[#FFD700]/15 border border-[#FFD700]/40 px-2 py-0.5 rounded-full">
                                {t('lucky_pack.label')}
                            </span>
                            {countdown && !hasEntered && (
                                <span className="inline-flex items-center gap-1 text-[10px] text-[#A0AAB2]">
                                    <Clock className="w-3 h-3" />
                                    {countdown}
                                </span>
                            )}
                            {hasEntered && !isSettled && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#39FF14] bg-[#39FF14]/10 px-2 py-0.5 rounded-full">
                                    <CheckCircle2 className="w-3 h-3" /> {t('lucky_pack.you_are_in')}
                                </span>
                            )}
                            {isSettled && (
                                <span
                                    className={`inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 rounded-full ${myEntry.status === 'won' ? 'bg-[#39FF14]/20 text-[#39FF14]' : myEntry.status === 'refund' ? 'bg-[#FFD700]/20 text-[#FFD700]' : 'bg-[#FF3B30]/20 text-[#FF3B30]'}`}
                                >
                                    <Trophy className="w-3 h-3" />
                                    {myEntry.status === 'won'
                                        ? t('lucky_pack.you_won', { amount: Number(myEntry.payout_amount || 0).toFixed(2), hits: myEntry.hits_count })
                                        : myEntry.status === 'refund'
                                            ? t('lucky_pack.refunded', { amount: Number(myEntry.payout_amount || 0).toFixed(2) })
                                            : t('lucky_pack.no_hit', { hits: myEntry.hits_count })}
                                </span>
                            )}
                        </div>

                        <p className="text-white font-black text-base sm:text-lg leading-tight">
                            {pack.name || 'Lucky 5'} — ${entry.toFixed(0)} {t('lucky_pack.in')} → ${p5.toFixed(0)} {t('lucky_pack.out')}
                        </p>
                        <p className="text-[#A0AAB2] text-xs sm:text-sm mt-0.5">
                            {t('lucky_pack.pick_blurb')}
                        </p>

                        {/* Payout tiers */}
                        <div className="grid grid-cols-3 gap-2 mt-3">
                            {[
                                { hits: 3, amount: p3, color: '#A0AAB2', testId: 'lucky-tier-3' },
                                { hits: 4, amount: p4, color: '#FFD700', testId: 'lucky-tier-4' },
                                { hits: 5, amount: p5, color: '#39FF14', testId: 'lucky-tier-5' },
                            ].map(tier => (
                                <div
                                    key={tier.hits}
                                    className="rounded-lg border bg-[#0a0a0a]/60 p-2 text-center"
                                    style={{ borderColor: `${tier.color}40` }}
                                    data-testid={tier.testId}
                                >
                                    <p className="text-[10px] text-[#A0AAB2] uppercase tracking-wider font-bold">
                                        {tier.hits} {t('lucky_pack.hits')}
                                    </p>
                                    <p className="text-lg font-mono font-black" style={{ color: tier.color }}>
                                        ${tier.amount.toFixed(0)}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {/* The 5 picks (compact list) */}
                        <ul className="mt-3 space-y-1">
                            {(pack.picks || []).map((p, i) => {
                                const result = p.result;
                                const dot = result === 'win' ? '#39FF14' : result === 'loss' ? '#FF3B30' : result === 'void' ? '#FFD700' : '#666';
                                return (
                                    <li
                                        key={`${pack.id}-${i}`}
                                        className="flex items-center gap-2 text-xs"
                                        data-testid={`lucky-pick-${i}`}
                                    >
                                        <span
                                            className="inline-block w-2 h-2 rounded-full shrink-0"
                                            style={{ background: dot, boxShadow: result ? `0 0 6px ${dot}` : 'none' }}
                                        />
                                        <span className="text-white font-semibold truncate">{sideLabel(p)}</span>
                                        <span className="text-[#A0AAB2] text-[11px] truncate">
                                            ({p.away_team} @ {p.home_team})
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>

                        {/* Action */}
                        <div className="mt-4">
                            {!hasEntered ? (
                                <Button
                                    onClick={() => setOpenDialog(true)}
                                    className="w-full font-black text-base h-12"
                                    style={{ background: 'linear-gradient(135deg, #FFD700, #FF007F)', color: 'black' }}
                                    data-testid="lucky-pack-enter-btn"
                                >
                                    <Ticket className="w-5 h-5 mr-2" />
                                    {t('lucky_pack.enter_for', { amount: entry.toFixed(2) })}
                                </Button>
                            ) : (
                                <p className="text-center text-xs text-[#A0AAB2] py-1">
                                    {isSettled
                                        ? t('lucky_pack.settled_msg')
                                        : t('lucky_pack.entered_msg', { time: fmtCT(myEntry.entered_at) })}
                                </p>
                            )}
                        </div>
                    </div>
                </motion.div>
            </AnimatePresence>

            {/* Win Share Dialog — auto-opens once for 4/5 or 5/5 wins */}
            <LuckyPackWinShareDialog myEntry={myEntry} />

            {/* Confirm Dialog */}
            <Dialog open={openDialog} onOpenChange={setOpenDialog}>
                <DialogContent className="bg-[#0e0e0e] border-white/10 text-white max-w-md" data-testid="lucky-pack-confirm-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-white text-xl font-black flex items-center gap-2">
                            <Ticket className="w-5 h-5 text-[#FFD700]" />
                            {t('lucky_pack.confirm_title')}
                        </DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            {t('lucky_pack.confirm_desc', { amount: entry.toFixed(2) })}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="rounded-lg bg-[#0a0a0a] border border-white/10 p-3">
                        <p className="text-[10px] uppercase tracking-wider text-[#A0AAB2] mb-2 font-bold">
                            {t('lucky_pack.your_picks')}
                        </p>
                        <ul className="space-y-1">
                            {(pack.picks || []).map((p, i) => (
                                <li key={i} className="text-xs text-white">
                                    <span className="text-[#FFD700] font-bold mr-1">{i + 1}.</span>
                                    {sideLabel(p)} <span className="text-[#A0AAB2]">— {p.away_team} @ {p.home_team}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <DialogFooter className="flex-row gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setOpenDialog(false)}
                            className="flex-1 border-white/10 text-white hover:bg-white/5"
                            data-testid="lucky-pack-cancel-btn"
                        >
                            <X className="w-4 h-4 mr-1" /> {t('common.cancel')}
                        </Button>
                        <Button
                            onClick={submit}
                            disabled={submitting}
                            className="flex-1 font-black"
                            style={{ background: 'linear-gradient(135deg, #FFD700, #FF007F)', color: 'black' }}
                            data-testid="lucky-pack-confirm-btn"
                        >
                            {submitting ? t('common.loading') : t('lucky_pack.lock_in', { amount: entry.toFixed(2) })}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default LuckyPackBanner;
